/**
 * NTP-over-HTTP time synchronization utility.
 *
 * Browsers cannot connect directly to UDP NTP servers. This module instead
 * queries an HTTP endpoint that returns an ISO-8601 datetime field (compatible
 * with worldtimeapi.org or any custom endpoint). The round-trip network latency
 * is measured and used to estimate the true server time at the moment of response.
 *
 * The calculated skew (serverTime - localTime) is stored in memory. All
 * time-sensitive operations should use `getNtpNow()` instead of `Date.now()`.
 *
 * Usage:
 *   // On app boot (e.g., in App.tsx):
 *   syncNtp({ enabled: true, endpointUrl: '...', syncIntervalMin: 15 });
 *
 *   // In time-sensitive code:
 *   const now = getNtpNow(); // same interface as Date.now()
 */

import type { NtpSettings } from '@/stores/settings-store';

// ── Internal state ────────────────────────────────────────────────────────────

/** Difference to add to Date.now() to approximate server time (ms). */
let skewMs = 0;

/** Last successful sync timestamp (Date.now() local). */
let lastSyncAt: number | null = null;

/** Status of the most recent sync attempt. */
let syncStatus: 'idle' | 'syncing' | 'ok' | 'error' = 'idle';

/** Human-readable error from last failed sync. */
let lastError: string | null = null;

/** Reference to the periodic re-sync interval timer. */
let syncIntervalHandle: ReturnType<typeof setInterval> | null = null;

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns the current time in milliseconds, adjusted by the last calculated
 * NTP skew. Falls back to plain `Date.now()` if NTP is disabled or not yet
 * synced (skew is 0 by default).
 */
export function getNtpNow(): number {
  return Date.now() + skewMs;
}

/**
 * Returns a snapshot of the current NTP sync state.
 * Useful for displaying status in the settings UI.
 */
export function getNtpStatus() {
  return {
    skewMs,
    lastSyncAt,
    syncStatus: syncStatus as typeof syncStatus,
    lastError,
  };
}

/**
 * Performs a single sync against the configured endpoint.
 *
 * **Supported response formats:**
 * - `{ ts: <unix ms> }` — own server-time Edge Function (preferred)
 * - `{ datetime: "<ISO-8601>" }` — worldtimeapi.org-compatible
 * - `{ utc_datetime: "<ISO-8601>" }` — worldtimeapi.org-compatible
 *
 * One-way latency (rtt/2) is applied to estimate the true server time.
 */
export async function syncNtpOnce(endpointUrl: string): Promise<void> {
  syncStatus = 'syncing';
  lastError = null;

  const t0 = Date.now();
  let response: Response;
  try {
    response = await fetch(endpointUrl, { method: 'GET', cache: 'no-store' });
  } catch (err) {
    syncStatus = 'error';
    lastError = err instanceof Error ? err.message : 'Error de red';
    throw new Error(lastError);
  }
  const t1 = Date.now();
  const rttMs = t1 - t0;

  if (!response.ok) {
    syncStatus = 'error';
    lastError = `HTTP ${response.status}`;
    throw new Error(lastError);
  }

  let body: Record<string, unknown>;
  try {
    body = await response.json() as Record<string, unknown>;
  } catch {
    syncStatus = 'error';
    lastError = 'El endpoint no retornó JSON válido';
    throw new Error(lastError);
  }

  // Strategy 1: { ts: <unix ms> }  — own server-time function
  if (typeof body['ts'] === 'number') {
    skewMs = (body['ts'] + rttMs / 2) - t1;
    lastSyncAt = t1;
    syncStatus = 'ok';
    return;
  }

  // Strategy 2: { utc_datetime | datetime: "<ISO-8601>" } — worldtimeapi.org
  const raw = (body['utc_datetime'] ?? body['datetime']) as string | undefined;
  if (!raw) {
    syncStatus = 'error';
    lastError = 'El endpoint no retornó los campos esperados (ts, datetime o utc_datetime)';
    throw new Error(lastError);
  }

  const serverMs = new Date(raw).getTime();
  if (isNaN(serverMs)) {
    syncStatus = 'error';
    lastError = `Fecha inválida retornada: "${raw}"`;
    throw new Error(lastError);
  }

  skewMs = (serverMs + rttMs / 2) - t1;
  lastSyncAt = t1;
  syncStatus = 'ok';
}

/**
 * Initialises NTP synchronisation according to the provided settings.
 * Safe to call multiple times — stops any existing interval before starting a new one.
 * Call this from `App.tsx` whenever the NTP settings change.
 */
export function syncNtp(settings: NtpSettings): void {
  // Stop any previous periodic sync
  if (syncIntervalHandle !== null) {
    clearInterval(syncIntervalHandle);
    syncIntervalHandle = null;
  }

  if (!settings.enabled) {
    // Reset skew so we fall back to system clock
    skewMs = 0;
    lastSyncAt = null;
    syncStatus = 'idle';
    lastError = null;
    return;
  }

  // Sync immediately
  syncNtpOnce(settings.endpointUrl).catch(() => { /* status already captured */ });

  // Schedule periodic re-sync
  if (settings.syncIntervalMin > 0) {
    syncIntervalHandle = setInterval(
      () => syncNtpOnce(settings.endpointUrl).catch(() => {}),
      settings.syncIntervalMin * 60 * 1000,
    );
  }
}
